import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authApi = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    updateProfile: (data) => api.put('/auth/profile', null, { params: data }),
};

// Parcels API
export const parcelsApi = {
    create: (data) => api.post('/parcels', data),
    getAll: (status) => api.get('/parcels', { params: status ? { status } : {} }),
    getAvailable: () => api.get('/parcels/available'),
    getById: (id) => api.get(`/parcels/${id}`),
    accept: (id) => api.post(`/parcels/${id}/accept`),
    updateStatus: (id, status) => api.post(`/parcels/${id}/status`, null, { params: { new_status: status } }),
    track: (id) => api.get(`/parcels/track/${id}`),
    calculatePrice: (data) => api.post('/calculate-price', null, { params: data }),
    downloadInvoice: (id) => api.get(`/parcels/${id}/invoice`, { responseType: 'blob' }),
    confirmDelivery: (id, code) => api.post(`/parcels/${id}/confirm-delivery`, { code }),
    updateLocation: (id, lat, lng) => api.post(`/parcels/${id}/update-location`, null, { params: { lat, lng } }),
    getLocation: (id) => api.get(`/parcels/${id}/location`),
};

// Messages API
export const messagesApi = {
    send: (data) => api.post('/messages', data),
    getByParcel: (parcelId) => api.get(`/messages/${parcelId}`),
};

// Payments API
export const paymentsApi = {
    createSession: (data) => api.post('/payments/create-session', data),
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
    getHistory: () => api.get('/payments/history'),
};

// Price Offers API (Système de prix négocié avec contre-offres)
export const offersApi = {
    makeOffer: (parcelId, carrierPrice) => api.post(`/parcels/${parcelId}/offer`, { carrier_price: carrierPrice }),
    getOffers: (parcelId) => api.get(`/parcels/${parcelId}/offers`),
    acceptOffer: (parcelId, offerId) => api.post(`/parcels/${parcelId}/offers/${offerId}/accept`),
    rejectOffer: (parcelId, offerId) => api.post(`/parcels/${parcelId}/offers/${offerId}/reject`),
    // Contre-offres
    makeCounterOffer: (parcelId, offerId, counterPrice, message) => 
        api.post(`/parcels/${parcelId}/offers/${offerId}/counter`, { counter_price: counterPrice, message }),
    acceptCounterOffer: (parcelId, offerId) => 
        api.post(`/parcels/${parcelId}/offers/${offerId}/accept-counter`),
};

// Admin API
export const adminApi = {
    getUsers: () => api.get('/admin/users'),
    getParcels: () => api.get('/admin/parcels'),
    getStats: () => api.get('/admin/stats'),
    deleteUser: (id) => api.delete(`/admin/users/${id}`),
    // Payouts (Versements)
    getPayouts: (status) => api.get('/admin/payouts', { params: status ? { status } : {} }),
    getPayoutStats: () => api.get('/admin/payouts/stats'),
    updatePayout: (id, data) => api.put(`/admin/payouts/${id}`, data),
    getFinancialReport: (period = 'month') => api.get(`/admin/financial-report?period=${period}`),
};

// Carrier Earnings API
export const earningsApi = {
    getPayouts: () => api.get('/carrier/payouts'),
    getEarnings: () => api.get('/carrier/earnings'),
    getHistory: () => api.get('/carrier/history'),
};

// Google Places API (Autocomplete)
export const placesApi = {
    autocomplete: (input) => api.get('/places/autocomplete', { params: { input } }),
    getDetails: (placeId) => api.get(`/places/details/${placeId}`),
};

// Reviews API
export const reviewsApi = {
    create: (data) => api.post('/reviews', data),
    getUserReviews: (userId) => api.get(`/reviews/user/${userId}`),
    getParcelReviews: (parcelId) => api.get(`/reviews/parcel/${parcelId}`),
    getPendingReviews: () => api.get('/reviews/my-pending'),
};

// Trips API (Trajets)
export const tripsApi = {
    create: (data) => api.post('/trips', data),
    getAll: (params) => api.get('/trips', { params }),
    getMy: () => api.get('/trips/my'),
    getById: (id) => api.get(`/trips/${id}`),
    update: (id, data) => api.put(`/trips/${id}`, data),
    delete: (id) => api.delete(`/trips/${id}`),
    complete: (id) => api.post(`/trips/${id}/complete`),
};

// Matching API (Auto-matching)
export const matchingApi = {
    getTripsForParcel: (parcelId) => api.get(`/matching/trips-for-parcel/${parcelId}`),
    getParcelsForTrip: (tripId) => api.get(`/matching/parcels-for-trip/${tripId}`),
    suggestTrips: (params) => api.get('/matching/suggest-for-sender', { params }),
};

// Identity Verification API (Stripe Identity)
export const identityApi = {
    createSession: (returnUrl) => api.post('/identity/create-session', { return_url: returnUrl }),
    getStatus: () => api.get('/identity/status'),
    checkSession: (sessionId) => api.post(`/identity/check/${sessionId}`),
};

// Notifications API
export const notificationsApi = {
    getAll: (limit = 20) => api.get('/notifications', { params: { limit } }),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markAsRead: (id) => api.patch(`/notifications/${id}/read`),
    markAllAsRead: () => api.patch('/notifications/read-all'),
};

export default api;
